// The "Yapılacaklar" drawer (Lore Nefes docs/discovery/
// masa-yapilacaklar-cekmecesi.md): what the person at the desk has to
// do today, read from their own Lore tasks. Step 1 is the sign-in —
// each person connects their own Lore account from here, in a popup,
// and nefes-api keeps the token. Step 2 is the list: the person's open
// tasks due today or earlier, oldest first, each with "Yapıldı"; a
// tick can be undone for a few seconds. Masa reads the tasks as they
// are — whoever opens a task decides what it says.

import {useCallback, useEffect, useRef, useState} from "react";
import {
  ApiRequestError, completeLoreTask, disconnectLore, getLoreStatus, getLoreTodo,
  reopenLoreTask, startLoreConnect,
} from "../api/client";
import type {LoreStatus, LoreTodo, LoreTodoTask} from "../api/contract";
import {Sheet} from "../actions/Sheets";

const POLL_MS = 2000;
const UNDO_MS = 8000;

const errorText = (e: unknown) =>
  e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);

// The door answers 409 with detail.lore when the stored connection is
// gone or Lore no longer accepts its token: back to the connect button.
const loreGone = (e: unknown) =>
  e instanceof ApiRequestError && e.status === 409 &&
  typeof (e.detail as {lore?: unknown} | undefined)?.lore === "string";

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("tr-TR", {day: "numeric", month: "long"}).format(d);
}

// The Istanbul calendar day of an instant (Türkiye is UTC+3, no DST).
const istDay = (iso: string) =>
  new Date(Date.parse(iso) + 3 * 3600e3).toISOString().slice(0, 10);

function dueLabel(task: LoreTodoTask, today: string): string {
  if (!task.dueAt) return "";
  return istDay(task.dueAt) === today ? "Bugün" : `Gecikti · ${formatWhen(task.dueAt)}`;
}

interface Toast {text: string; undo?: LoreTodoTask}

export function TodoDrawer({onClose}: {onClose: () => void}) {
  const [status, setStatus] = useState<LoreStatus | null>(null);
  const [todo, setTodo] = useState<LoreTodo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [gone, setGone] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const popup = useRef<Window | null>(null);
  const toastTimer = useRef<number | null>(null);
  const seenConnectedAt = useRef<string | undefined>(undefined);

  // Reads the status; a fresh connection ends the connect (the popup
  // closes, the button comes back) and clears a "gone" verdict.
  const refresh = useCallback(async (signal?: AbortSignal) => {
    try {
      const {data} = await getLoreStatus(signal);
      if (signal?.aborted) return;
      setStatus(data);
      setError(null);
      if (data.connected && !data.expired) {
        popup.current?.close();
        popup.current = null;
        setBusy(false);
        if (data.connectedAt !== seenConnectedAt.current) {
          seenConnectedAt.current = data.connectedAt;
          setGone(false);
        }
      }
    } catch (e) {
      if (signal?.aborted) return;
      setError(errorText(e));
    }
  }, []);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const {data} = await getLoreTodo(signal);
      if (signal?.aborted) return;
      setTodo(data);
      setError(null);
    } catch (e) {
      if (signal?.aborted) return;
      if (loreGone(e)) {
        setGone(true);
        setTodo(null);
        return;
      }
      setError(errorText(e));
    }
  }, []);

  useEffect(() => {
    const ctl = new AbortController();
    // refresh() only sets state after its await — the rule cannot see that.
    // oxlint-disable-next-line react/set-state-in-effect
    void refresh(ctl.signal);
    return () => ctl.abort();
  }, [refresh]);

  const connected = status?.connected === true && status.expired !== true && !gone;

  // The list, once connected.
  useEffect(() => {
    if (!connected) return;
    const ctl = new AbortController();
    // load() only sets state after its await — the rule cannot see that.
    // oxlint-disable-next-line react/set-state-in-effect
    void load(ctl.signal);
    return () => ctl.abort();
  }, [connected, load]);

  // While the connect popup is open: the callback page posts a
  // message on success, and the status is polled as a fallback (the
  // popup may be closed, or the message blocked). Both end when the
  // person is connected or the popup is gone.
  useEffect(() => {
    if (!busy) return;
    const onMessage = (e: MessageEvent) => {
      const d = e.data as {type?: unknown; ok?: unknown} | null;
      if (d?.type === "nefes-lore" && d.ok === true) void refresh();
    };
    window.addEventListener("message", onMessage);
    const timer = window.setInterval(() => {
      void refresh();
      if (popup.current?.closed) {
        popup.current = null;
        setBusy(false);
      }
    }, POLL_MS);
    return () => {
      window.removeEventListener("message", onMessage);
      window.clearInterval(timer);
    };
  }, [busy, refresh]);

  useEffect(() => () => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
  }, []);

  const showToast = (t: Toast) => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    setToast(t);
    toastTimer.current = window.setTimeout(() => {
      toastTimer.current = null;
      setToast(null);
    }, UNDO_MS);
  };

  const connect = async () => {
    // Opened in the click itself, so the browser allows it; the
    // address comes once the door has minted the request.
    const w = window.open("", "nefes-lore", "popup,width=520,height=760");
    popup.current = w;
    setBusy(true);
    setError(null);
    try {
      const {data} = await startLoreConnect();
      if (w && !w.closed) w.location.href = data.url;
      else window.open(data.url, "nefes-lore", "popup,width=520,height=760");
    } catch (e) {
      w?.close();
      popup.current = null;
      setBusy(false);
      setError(errorText(e));
    }
  };

  const disconnect = async () => {
    setError(null);
    try {
      await disconnectLore();
      setTodo(null);
      setToast(null);
      await refresh();
    } catch (e) {
      setError(errorText(e));
    }
  };

  // "Yapıldı": the task leaves the list; a recurring task reopens at
  // its next slot in Lore, so there is nothing to undo for it.
  const done = async (task: LoreTodoTask) => {
    setPending(task.taskId);
    setError(null);
    try {
      const {data} = await completeLoreTask(task.taskId);
      setTodo((t) => t && {...t, tasks: t.tasks.filter((x) => x.taskId !== task.taskId)});
      if (data.recurringRolledTo) {
        showToast({text: `Yapıldı: ${task.title} · tekrarlayan görev, sıradaki ` +
          `${formatWhen(data.recurringRolledTo)}`});
      } else {
        showToast({text: `Yapıldı: ${task.title}`, undo: task});
      }
    } catch (e) {
      if (loreGone(e)) {
        setGone(true);
        setTodo(null);
      } else {
        setError(errorText(e));
      }
    } finally {
      setPending(null);
    }
  };

  const reopen = async () => {
    const task = toast?.undo;
    if (!task) return;
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    toastTimer.current = null;
    setToast(null);
    setPending(task.taskId);
    setError(null);
    try {
      await reopenLoreTask(task.taskId);
      await load();
    } catch (e) {
      if (loreGone(e)) {
        setGone(true);
        setTodo(null);
      } else {
        setError(errorText(e));
      }
    } finally {
      setPending(null);
    }
  };

  const who = todo?.email ? todo.email :
    status?.connectedAt ? formatWhen(status.connectedAt) : "";

  return (
    <Sheet title="Yapılacaklar" onClose={onClose} className="todo-sheet">
      {error && <p className="banner warn">{error}</p>}
      {status === null && !error && <p className="muted">Yükleniyor…</p>}
      {status !== null && !connected && (
        <div className="todo-empty">
          {gone ?
            <p>Lore bağlantısı artık geçerli değil. Yeniden bağlanın.</p> :
            status.expired ?
              <p>Lore bağlantısının süresi doldu. Yeniden bağlanın.</p> :
              <p>Yapılacaklar listesi Lore'daki görevlerinizden gelir. Kendi Lore
                hesabınızla bağlanın.</p>}
          <button className="primary big" disabled={busy} onClick={() => void connect()}>
            {busy ? "Lore penceresi açık…" : "Lore'a bağlan"}
          </button>
          {busy && <p className="muted small">Açılan pencerede Lore'a giriş yapın.</p>}
        </div>
      )}
      {connected && (
        <div className="todo-body">
          <p className="muted small">
            Lore'a bağlı{who ? ` · ${who}` : ""}
            {todo?.project.name ? ` · ${todo.project.name}` : ""}
          </p>
          {todo === null && !error && <p className="muted">Görevler yükleniyor…</p>}
          {todo !== null && todo.tasks.length === 0 && <p>Bugün için açık görev yok.</p>}
          {todo !== null && todo.tasks.length > 0 && (
            <ul className="todo-list">
              {todo.tasks.map((t) => (
                <li key={t.taskId} className="todo-item">
                  <div className="todo-text">
                    <div className="todo-title">
                      {t.priority === "high" && <span className="chip flag">Öncelikli</span>}
                      {t.priority === "high" ? " " : ""}{t.title}
                    </div>
                    <div className="muted small">
                      {dueLabel(t, todo.today)}
                      {t.tags.length > 0 ? ` · ${t.tags.join(", ")}` : ""}
                    </div>
                    {t.description && <div className="small todo-desc">{t.description}</div>}
                    {t.formUrl && (
                      <a className="todo-form" href={t.formUrl} target="_blank" rel="noreferrer">
                        Formu yazdır ↗
                      </a>
                    )}
                  </div>
                  <button className="primary" disabled={pending !== null}
                    onClick={() => void done(t)}>
                    {pending === t.taskId ? "…" : "Yapıldı"}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {toast && (
            <div className="todo-toast">
              <span>{toast.text}</span>
              {toast.undo && (
                <button className="link" disabled={pending !== null}
                  onClick={() => void reopen()}>Geri al</button>
              )}
            </div>
          )}
          <div className="sheet-actions">
            <button disabled={pending !== null} onClick={() => void load()}>Yenile</button>
            <button onClick={() => void disconnect()}>Bağlantıyı kes</button>
          </div>
        </div>
      )}
    </Sheet>
  );
}
